/**
 * Preguntas de instrumento (`/api/preguntas`).
 *
 * Encuesta y grupo focal solo admiten preguntas de OPCIÓN ÚNICA: la conversión a
 * nota cuenta una opción por persona, y con texto libre o casillas no hay
 * distribución que convertir.
 *
 * Las opciones de una pregunta de opinión no son texto libre — salen de la
 * rúbrica. Cualquier otra etiqueta queda fuera de la tabla de valoración y no
 * puntúa.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type Pregunta from '@/interfaces/Preguntas'
import { http } from '@/services/http'

/**
 * Consulta preguntas · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Pregunta[]
 */
export async function getPreguntasService(
  url: string,
  params?: { id_instrumento?: number; id_grupo_interes?: number; banco?: boolean }
): Promise<Pregunta[]> {
  const query = new URLSearchParams()
  if (params?.id_instrumento) query.append('id_instrumento', String(params.id_instrumento))
  if (params?.id_grupo_interes) query.append('id_grupo_interes', String(params.id_grupo_interes))
  // banco=1 → solo las preguntas sin instrumento (el banco propiamente dicho).
  if (params?.banco) query.append('banco', '1')
  const endpoint = query.toString() ? `${url}?${query.toString()}` : url
  const result = await http.get<Pregunta[]>(endpoint)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Consulta preguntas · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Pregunta | null
 */
export async function getPreguntaByIdService(url: string, id: number): Promise<Pregunta | null> {
  const result = await http.get<Pregunta>(`${url}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea preguntas.
 *
 * @returns Pregunta | null
 */
export async function crearPreguntaService(
  url: string,
  payload: Omit<Pregunta, 'id_pregunta'>
): Promise<Pregunta | null> {
  const body = {
    id_instrumento: payload.id_instrumento,
    // Aspecto-indicador de la pregunta y su origen en el banco: sin enviarlos
    // no habría con qué calificar por indicador ni qué reutilizar después.
    id_indicador: payload.id_indicador ?? null,
    id_pregunta_origen: payload.id_pregunta_origen ?? null,
    id_grupo_interes: payload.id_grupo_interes,
    enunciado: payload.enunciado,
    pregunta: payload.enunciado,
    tipo_respuesta: payload.tipo_respuesta,
    tipo: payload.tipo_respuesta,
    opciones: payload.opciones,
    orden: payload.orden
  }
  const result = await http.post<Pregunta>(url, body)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Actualiza preguntas.
 *
 * @returns Pregunta | null
 */
export async function actualizarPreguntaService(
  url: string,
  id: number,
  payload: Partial<Pregunta>
): Promise<Pregunta | null> {
  const body = {
    ...payload,
    pregunta: payload.enunciado ?? payload.pregunta,
    tipo: payload.tipo_respuesta ?? payload.tipo
  }
  const result = await http.put<Pregunta>(`${url}/${id}`, body)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina preguntas · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarPreguntaService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url}/${id}`)
  return result.ok
}
